import React, { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  SETTINGS_COLUMN_HEADING,
  SETTINGS_NAV_LABELS,
  SETTINGS_PATHS,
  type SettingsPageId,
} from '@/lib/settingsPaths';
import { useAccountRecord } from '@/pages/account/AccountRecordContext';
import {
  searchSettings,
  visibleSettingsPages,
} from '@/pages/settings/settingsSearchIndex';

interface SettingsSidebarProps {
  /** Render for the mobile nav drawer — always expanded, taller rows for touch. */
  inDrawer?: boolean;
  /** Called after a nav row navigates, so the drawer can close itself. */
  onNavigated?: () => void;
}

/**
 * AR-390 — the Settings module's Level 2 column.
 *
 * The same column the Account center has, with one difference the decision
 * calls out: it KEEPS its heading (D-ACCT-28). Account's head is the identity
 * block — the photo, the name and the brand say whose account it is — and
 * Settings has no such block, so nothing else would name the module.
 *
 * Head: the heading, then the settings search. Body: the four pages, in order.
 * Proposal Builder and Payments + CardVault are parked (D-ACCT-38) and are
 * absent from both — they render by link only.
 */
export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  inDrawer = false,
  onNavigated,
}) => {
  const [location, navigate] = useLocation();
  const { setFocusCard } = useAccountRecord();
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState('');

  const pages = useMemo(() => visibleSettingsPages(), []);
  const hits = useMemo(() => searchSettings(query), [query]);

  const isActive = (page: SettingsPageId) => location === SETTINGS_PATHS[page];

  const go = (path: string, card?: string) => {
    navigate(path);
    setQuery('');
    // A result opens the card holding the field: the page scrolls to it and
    // marks it for a moment.
    setFocusCard(card ?? null);
    onNavigated?.();
  };

  if (collapsed && !inDrawer) {
    return (
      <div
        data-app-chrome
        className="flex w-8 flex-col items-center border-r border-gray-200 bg-gray-50/50 pt-4 dark:border-gray-700 dark:bg-gray-900/50"
      >
        <button
          onClick={() => setCollapsed(false)}
          className="rounded p-1 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          title="Expand Settings sidebar"
          aria-label="Expand Settings sidebar"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-app-chrome
      data-settings-sidebar
      className={cn(
        'flex flex-col bg-gray-50/50 dark:bg-gray-900/50',
        inDrawer ? 'w-full' : 'min-h-0 w-60 border-r border-gray-200 dark:border-gray-700',
      )}
    >
      <div className={cn('px-3 pb-2', inDrawer ? 'pt-3' : 'pt-4')}>
        {!inDrawer && (
          <button
            onClick={() => setCollapsed(true)}
            className="mb-1 rounded p-1 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        {/* The column's own heading — nothing else names this module. */}
        <div className="mb-1.5 border-b border-gray-200 px-1.5 pb-2.5 dark:border-gray-700">
          <b className="block text-[14.5px] leading-tight text-gray-900 dark:text-gray-100">
            {SETTINGS_COLUMN_HEADING}
          </b>
        </div>

        {/* The settings search. */}
        <div className="px-0.5 pb-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search settings"
            aria-label="Search settings"
            autoComplete="off"
            className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[13.5px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
          {query.trim() ? (
            <div className="mt-1.5 flex flex-col gap-0.5" data-settings-search-results>
              {hits.length ? (
                hits.map((h) => (
                  <button
                    key={`${h.label}-${h.page}-${h.card}`}
                    type="button"
                    onClick={() => go(h.path, h.focus)}
                    className="w-full rounded-md px-2 py-1 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <b className="block text-[13.5px] font-semibold leading-tight text-gray-900 dark:text-gray-100">
                      {h.label}
                    </b>
                    <small className="block text-[12px] text-gray-500 dark:text-gray-400">
                      {h.card ? `${h.pageLabel}, ${h.card}` : h.pageLabel}
                    </small>
                  </button>
                ))
              ) : (
                <p className="px-2 py-1 text-[12.5px] text-gray-500 dark:text-gray-400">
                  No setting matches that.
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <nav
        className={cn('space-y-0.5 px-3 pb-2', !inDrawer && 'flex-1 overflow-y-auto')}
        aria-label="Settings"
      >
        {pages.map((page) => {
          const active = isActive(page);
          return (
            <button
              key={page}
              type="button"
              onClick={() => go(SETTINGS_PATHS[page])}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex w-full items-center rounded-lg px-3 text-left text-sm transition-colors',
                inDrawer ? 'min-h-11 py-2.5' : 'py-2',
                active
                  ? 'bg-orange-100 font-medium text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
              )}
            >
              {SETTINGS_NAV_LABELS[page]}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default SettingsSidebar;
